<template>
  <div class="space-y-6">
    <div class="bg-white rounded-xl shadow-sm border border-neutral-200 p-6">
      <div class="flex items-center justify-between mb-4">
        <div>
          <h2 class="text-lg font-bold text-neutral-900">需求分类管理</h2>
          <p class="text-sm text-neutral-500 mt-1">
            管理发布需求 / 需求列表中使用的分类（如：课件制作、作业设计等）
          </p>
        </div>
        <button
          @click="openAddDialog"
          class="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-2"
        >
          <i class="fa fa-plus"></i>新增分类
        </button>
      </div>

      <div v-loading="loading" class="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div
          v-for="item in list"
          :key="item.id"
          class="flex items-center gap-4 bg-neutral-50 rounded-xl border border-neutral-200 p-4 hover:shadow-md transition-shadow"
        >
          <div
            class="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-primary/10 text-primary"
          >
            <i class="fa fa-lightbulb-o text-xl"></i>
          </div>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="font-bold text-neutral-900">{{ item.title }}</h3>
              <span
                class="text-xs px-2 py-0.5 rounded-full"
                :class="item.isEnable ? 'bg-green-100 text-green-700' : 'bg-neutral-200 text-neutral-500'"
              >{{ item.isEnable ? "启用" : "停用" }}</span>
            </div>
            <p class="text-sm text-neutral-500 mt-1 truncate">{{ item.description || "暂无描述" }}</p>
          </div>
          <div class="text-sm text-neutral-400 flex-shrink-0">排序 {{ item.orderNum }}</div>
          <div class="flex gap-2 flex-shrink-0">
            <button
              @click="openEditDialog(item)"
              class="px-3 py-1.5 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors text-sm"
            >编辑</button>
            <button
              @click="deleteItem(item)"
              class="px-3 py-1.5 bg-neutral-100 text-neutral-500 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors text-sm"
            >删除</button>
          </div>
        </div>
        <div v-if="!loading && list.length === 0" class="text-center py-12 text-neutral-400 text-sm col-span-full">
          暂无分类，点击右上角新增
        </div>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '编辑需求分类' : '新增需求分类'"
      width="480px"
      :close-on-click-modal="false"
      destroy-on-close
    >
      <div class="space-y-4">
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">分类名称 <span class="text-red-500">*</span></label>
          <input
            v-model="form.title"
            type="text"
            placeholder="如：课件制作"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          />
        </div>
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-neutral-700 mb-1">排序号</label>
            <input
              v-model.number="form.orderNum"
              type="number"
              class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
            />
          </div>
          <div class="flex items-end pb-2">
            <div class="flex items-center gap-2">
              <label class="text-sm font-medium text-neutral-700">启用：</label>
              <el-switch v-model="form.isEnable" />
            </div>
          </div>
        </div>
        <div>
          <label class="block text-sm font-medium text-neutral-700 mb-1">描述</label>
          <textarea
            v-model="form.description"
            rows="2"
            placeholder="分类简介"
            class="w-full px-4 py-2 rounded-lg border border-neutral-300 focus:ring-2 focus:ring-primary focus:border-primary text-sm"
          ></textarea>
        </div>
      </div>
      <template #footer>
        <div class="flex justify-end gap-3">
          <button @click="dialogVisible = false" class="px-4 py-2 rounded-lg border border-neutral-300 text-neutral-700 text-sm">取消</button>
          <button @click="save" class="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium">确定</button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ElMessageBox, ElMessage } from "element-plus";

export default {
  name: "DemandCategoryManager",
  data() {
    return {
      list: [],
      loading: false,
      dialogVisible: false,
      isEdit: false,
      form: { id: "", title: "", description: "", orderNum: 0, isEnable: true },
    };
  },
  created() {
    this.getList();
  },
  methods: {
    async getList() {
      this.loading = true;
      try {
        const data = await this.$aesEncrypt({});
        const res = await this.$axios({ url: "demandcat/admin/list", method: "POST", data: { data } });
        if (res.code === 200) this.list = res.data || [];
      } finally {
        this.loading = false;
      }
    },
    openAddDialog() {
      this.isEdit = false;
      this.form = { id: "", title: "", description: "", orderNum: (this.list.length + 1) * 10, isEnable: true };
      this.dialogVisible = true;
    },
    openEditDialog(item) {
      this.isEdit = true;
      this.form = { ...item };
      this.dialogVisible = true;
    },
    async save() {
      if (!this.form.title.trim()) {
        ElMessage.warning("请填写分类名称");
        return;
      }
      const payload = { ...this.form, title: this.form.title.trim() };
      if (!this.isEdit) delete payload.id;
      const data = await this.$aesEncrypt(payload);
      const res = await this.$axios({ url: "demandcat", method: "PUT", data: { data } });
      if (res.code === 200) {
        ElMessage.success(this.isEdit ? "编辑成功" : "新增成功");
        this.dialogVisible = false;
        this.getList();
      } else {
        ElMessage.error(res.msg || "保存失败");
      }
    },
    deleteItem(item) {
      ElMessageBox.confirm(`确定删除需求分类「${item.title}」吗？`, "删除分类", {
        confirmButtonText: "删除",
        cancelButtonText: "取消",
        type: "error",
      }).then(() => {
        this.$axios({ url: `demandcat/${item.id}`, method: "DELETE" }).then((res) => {
          if (res.code === 200) {
            ElMessage.success("删除成功");
            this.getList();
          } else {
            ElMessage.error(res.msg || "删除失败");
          }
        });
      });
    },
  },
};
</script>
